import * as TVL from "lightweight-charts";

export { TVL };

export interface TVLightSeries<T extends TVL.SeriesType = TVL.SeriesType> {
  type: T;
  data: TVL.SeriesDataItemTypeMap[T][];
  options: TVL.SeriesPartialOptionsMap[T];
  priceScaleId?: string;
  lineWidth?: number;
}

export interface TVLightProps {
  options: TVL.DeepPartial<TVL.ChartOptions>;
  height?: number;
  series: TVLightSeries[];
}

type SeriesDefinitionMap = {
  [K in TVL.SeriesType]: TVL.SeriesDefinition<K>;
};

/**
 * Smart tick mark formatter for the time axis.
 * - Year/Month: default format
 * - DayOfMonth: "DD Mon" (e.g. "29 Jan") — shown once per day
 * - Time: "HH:MM" format
 * - TimeWithSeconds: "HH:MM:SS" format
 */
export const smartTickMarkFormatter: TVL.TickMarkFormatter = (
  time: TVL.Time,
  tickMarkType: TVL.TickMarkType,
): string | null => {
  const ts = typeof time === "number" ? time * 1000 : new Date(time as string).getTime();
  const d = new Date(ts);

  const pad = (n: number) => n.toString().padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  switch (tickMarkType) {
    case 0: // Year
      return d.getUTCFullYear().toString();
    case 1: // Month
      return `${months[d.getUTCMonth()]} '${d.getUTCFullYear().toString().slice(2)}`;
    case 2: // DayOfMonth
      return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
    case 3: // Time
      return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    case 4: // TimeWithSeconds
      return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
    default:
      return null;
  }
};

/**
 * Default timeScale options with smart formatting and time visibility.
 */
export const defaultTimeScaleOptions: TVL.DeepPartial<TVL.TimeScaleOptions> = {
  timeVisible: true,
  tickMarkFormatter: smartTickMarkFormatter,
};

export const getSeriesDefinitionsForType = (type: TVL.SeriesType) => {
  const typeMap: SeriesDefinitionMap = {
    Line: TVL.LineSeries,
    Area: TVL.AreaSeries,
    Bar: TVL.BarSeries,
    Candlestick: TVL.CandlestickSeries,
    Histogram: TVL.HistogramSeries,
    Baseline: TVL.BaselineSeries,
    Custom: {
      type: "Custom",
      isBuiltIn: false,
      defaultOptions: TVL.customSeriesDefaultOptions,
    },
  };
  return typeMap[type];
};
